import React, { useCallback, useRef } from 'react';
import type { Highlight } from '../../types';
import HighlightBookmark from './HighlightBookmark';

interface HighlightBookmarksProps {
  highlights: Highlight[];
  activeIndex: number;
  paused: boolean;
  panelId: string;
  bookmarkIdPrefix: string;
  onSelect: (index: number) => void;
  onTogglePaused: () => void;
}

/**
 * Right-margin column: the paper-bookmark tablist + the "ver todos" overflow
 * handle + the autoplay control. Keyboard navigation moves between bookmarks
 * with ArrowUp/ArrowDown/Home/End (vertical orientation).
 */
const HighlightBookmarks: React.FC<HighlightBookmarksProps> = ({
  highlights,
  activeIndex,
  paused,
  panelId,
  bookmarkIdPrefix,
  onSelect,
  onTogglePaused,
}) => {
  const buttonsRef = useRef<Array<HTMLButtonElement | null>>([]);

  const focusBookmark = useCallback((i: number) => {
    const btn = buttonsRef.current[i];
    if (btn) btn.focus();
  }, []);

  const onKeyDown = useCallback(
    (i: number) => (e: React.KeyboardEvent<HTMLButtonElement>) => {
      const last = highlights.length - 1;
      switch (e.key) {
        case 'ArrowDown':
        case 'ArrowRight': {
          e.preventDefault();
          const target = i === last ? 0 : i + 1;
          focusBookmark(target);
          onSelect(target);
          break;
        }
        case 'ArrowUp':
        case 'ArrowLeft': {
          e.preventDefault();
          const target = i === 0 ? last : i - 1;
          focusBookmark(target);
          onSelect(target);
          break;
        }
        case 'Home': {
          e.preventDefault();
          focusBookmark(0);
          onSelect(0);
          break;
        }
        case 'End': {
          e.preventDefault();
          focusBookmark(last);
          onSelect(last);
          break;
        }
        default:
      }
    },
    [highlights.length, focusBookmark, onSelect],
  );

  return (
    <div
      className="psl-hl-bookmarks"
      role="tablist"
      aria-label="Destaques disponíveis"
      aria-orientation="vertical"
    >
      {highlights.map((h, i) => (
        <HighlightBookmark
          key={h.id}
          ref={(el) => {
            buttonsRef.current[i] = el;
          }}
          highlight={h}
          isActive={i === activeIndex}
          id={`${bookmarkIdPrefix}-${i}`}
          panelId={panelId}
          // Roving tabindex: only the active tab is reachable by Tab; the
          // rest are reached via ArrowUp/ArrowDown once inside the tablist.
          tabIndex={i === activeIndex ? 0 : -1}
          onSelect={() => onSelect(i)}
          onKeyDown={onKeyDown(i)}
        />
      ))}

      <button
        type="button"
        className="psl-hl-bm-overflow"
        // "ver todos" — out of scope this round; destination wires up once the
        // archive page exists. See HANDOFF §15.
        onClick={() => {
          if (typeof console !== 'undefined') {
            console.warn('[HighlightBookmarks] ver todos: archive page not wired up yet.');
          }
        }}
      >
        ver todos
        <span className="psl-hl-bm-overflow__all">— no índice ›</span>
      </button>

      <button
        type="button"
        className={`psl-hl-bm-autoplay${paused ? ' is-paused' : ''}`}
        aria-label={paused ? 'Retomar avanço automático' : 'Pausar avanço automático'}
        aria-pressed={paused}
        onClick={onTogglePaused}
      >
        {paused ? 'retomar' : 'auto'}
      </button>
    </div>
  );
};

export default HighlightBookmarks;
